import { useCallback } from 'react';
import { useAuth } from './useAuth';
import { trpc } from '../lib/trpc';
import { canManageVenue } from '@wineryxplorer/shared';
import type { Venue } from '@wineryxplorer/shared';

/**
 * Hook for checking venue management permissions
 * Simplified approach - relies on natural navigation and tRPC cache invalidation
 * for permission updates rather than polling
 */

interface VenuePermissionOptions {
  venueId: string;
}

export const useVenuePermissions = (options: VenuePermissionOptions) => {
  const { user } = useAuth();
  const { venueId } = options;

  // Query to get current venue data
  const { data: venue, isLoading, error } = trpc.venue.getById.useQuery(
    { id: venueId },
    { enabled: Boolean(venueId && user) }
  );

  // Check if user has permission to manage the venue
  const hasPermission = useCallback((): boolean => {
    if (!user || !venue) {
      return false;
    }

    // Cast venue to expected type for permission check
    return canManageVenue(venue as unknown as Venue, user.role, user.id);
  }, [user, venue]);

  return {
    hasPermission: hasPermission(),
    venue,
    isLoading,
    error,
  };
};
